/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, EMPTY, throwError, timer } from 'rxjs'
import { catchError, filter, switchMap, take } from 'rxjs/operators'
import { UsuarioService } from '../services/usuario.service'
import { StorageService } from '../services/storage.service'
import { environment } from '../../environments/environment'

let isRedirecting = false
let isRefreshing = false
const refreshTokenSubject = new BehaviorSubject<string | null>(null)

const AUTH_WHITELIST = ['/privado/login', '/privado/candidato/cadastro', '/privado/candidato/refresh']

function addAuthHeaders(req: Parameters<HttpInterceptorFn>[0], token: string, lang: string) {
  return req.clone({
    headers: req.headers.set('Authorization', 'Bearer ' + token).set('Accept-Language', lang),
    withCredentials: false,
  })
}

function getTokenExpiry(token: string): number | null {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp
  } catch {
    return null
  }
}

function isWhitelisted(url: string): boolean {
  return AUTH_WHITELIST.some(path => url.includes(path))
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const usuarioService = inject(UsuarioService)
  const translateService = inject(TranslateService)
  const storage = inject(StorageService)
  const lang = translateService.currentLang || 'pt'

  // URLs externas (não são do backend) passam direto sem interceptação
  if (!req.url.startsWith(environment.baseUrlApi)) {
    return next(req)
  }

  // Whitelist e requests públicos passam direto, independente do estado do token
  if (isWhitelisted(req.url) || req.url.includes('/publico/')) {
    return next(req.clone({ headers: req.headers.set('Accept-Language', lang) }))
  }

  const token = storage.getToken()

  // Sem token: bloquear requests privados
  if (!token) {
    handleSessionExpired(usuarioService)
    return EMPTY
  }

  // Token expirado client-side: tentar refresh
  const expiry = getTokenExpiry(token)
  if (expiry !== null && Math.floor(Date.now() / 1000) >= expiry) {
    return handleRefreshAndRetry(req, next, lang, usuarioService, storage)
  }

  // Token válido: enviar request com Authorization
  return next(addAuthHeaders(req, token, lang)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handleRefreshAndRetry(req, next, lang, usuarioService, storage)
      }
      return throwError(() => error)
    })
  )
}

function handleRefreshAndRetry(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  lang: string,
  usuarioService: UsuarioService,
  storage: StorageService
) {
  if (isRefreshing) {
    // Aguardar refresh em andamento
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next(addAuthHeaders(req, token!, lang)))
    )
  }

  isRefreshing = true
  refreshTokenSubject.next(null)

  return usuarioService.refreshToken().pipe(
    switchMap(newToken => {
      isRefreshing = false
      refreshTokenSubject.next(newToken)
      return next(addAuthHeaders(req, newToken, lang))
    }),
    catchError(() => {
      isRefreshing = false
      // Outra aba pode ter feito refresh — verificar localStorage
      const freshToken = storage.getToken()
      const freshExp = freshToken ? getTokenExpiry(freshToken) : null
      if (freshExp && Math.floor(Date.now() / 1000) < freshExp) {
        refreshTokenSubject.next(freshToken)
        return next(addAuthHeaders(req, freshToken!, lang))
      }
      handleSessionExpired(usuarioService)
      return EMPTY
    })
  )
}

function handleSessionExpired(usuarioService: UsuarioService): void {
  if (isRedirecting) return
  isRedirecting = true
  usuarioService.timeout()
  timer(3000).subscribe(() => (isRedirecting = false))
}
